/**
 * Utility functions for checking form visibility based on dates
 */

/**
 * Checks if a form should be visible based on its hide date
 * @param {string} hideDate - The date string (YYYY-MM-DD) when the form should be hidden
 * @returns {boolean} - True if the form should be visible, false if it should be hidden
 */
export const isFormVisible = (hideDate) => {
  // If no hide date is set, form is always visible
  if (!hideDate || hideDate.trim() === "") {
    return true;
  }

  // Parse the hide date
  const hideDateObj = new Date(hideDate);
  const currentDate = new Date();

  // Set time to start of day for both dates to compare only dates, not times
  currentDate.setHours(0, 0, 0, 0);
  hideDateObj.setHours(0, 0, 0, 0);

  // Form is visible if current date is before or equal to hide date
  return currentDate <= hideDateObj;
};

/**
 * Checks multiple form visibility states at once
 * @param {Object} formDates - Object containing form names and their hide dates
 * @returns {Object} - Object with form names and their visibility states
 */
export const getFormVisibilityStates = (formDates) => {
  const visibilityStates = {};

  Object.keys(formDates).forEach((formName) => {
    visibilityStates[formName] = isFormVisible(formDates[formName]);
  });

  return visibilityStates;
};

/**
 * Checks if a specific form should be visible by name
 * @param {Object} formData - The form data object containing hide dates
 * @param {string} formName - The name of the form to check
 * @param {string} schoolName - Optional school name for school-specific restrictions
 * @returns {boolean} - True if the form should be visible
 */
export const isSpecificFormVisible = (
  formData,
  formName,
  schoolName = null
) => {
  if (!formData || !formData[formName]) {
    // Check for school-specific restrictions even when no date data exists
    return isSchoolAuthorizedForForm(formName, schoolName);
  }

  // First check date-based visibility
  const isDateVisible = isFormVisible(formData[formName]);

  // Then check school-specific restrictions
  const isSchoolAuthorized = isSchoolAuthorizedForForm(formName, schoolName);

  // Form is visible only if both conditions are met
  return isDateVisible && isSchoolAuthorized;
};

/**
 * Checks if a school is authorized to access a specific form
 * @param {string} formName - The name of the form to check
 * @param {string} schoolName - The school name to check authorization for
 * @returns {boolean} - True if the school is authorized or no restriction exists
 */
export const isSchoolAuthorizedForForm = (formName, schoolName) => {
  // Define school-specific restrictions
  // Note: Course Planning access is no longer controlled here — see
  // useCoursePlanningBetaAccess.js / the CcpBetaTesting collection.
  const SCHOOL_RESTRICTIONS = {};

  // Debug logging
  console.log("isSchoolAuthorizedForForm - formName:", formName);
  console.log("isSchoolAuthorizedForForm - schoolName:", schoolName);
  console.log(
    "isSchoolAuthorizedForForm - SCHOOL_RESTRICTIONS:",
    SCHOOL_RESTRICTIONS
  );

  // If no school name provided, default to not authorized for restricted forms
  if (!schoolName) {
    const result = !SCHOOL_RESTRICTIONS[formName]; // If form has restrictions, deny access
    console.log("isSchoolAuthorizedForForm - no schoolName, result:", result);
    return result;
  }

  // If the form has restrictions, check if the school is in the allowed list
  if (SCHOOL_RESTRICTIONS[formName]) {
    const allowedSchools = SCHOOL_RESTRICTIONS[formName];
    const result = allowedSchools.includes(schoolName);
    console.log("isSchoolAuthorizedForForm - allowedSchools:", allowedSchools);
    console.log(
      "isSchoolAuthorizedForForm - comparing schoolName:",
      schoolName,
      "with allowed schools"
    );
    console.log("isSchoolAuthorizedForForm - includes result:", result);
    return result;
  }

  // If no restrictions for this form, it's available to all schools
  console.log("isSchoolAuthorizedForForm - no restrictions, result: true");
  return true;
};
